// src/lib/stockStatus.js
// #357, Session 78g — one rule, previously three copies that disagreed.
//
// Stock Home, the Use screen and the Inventory Catalog each carried their own
// version of "is this item low or out." At Session 78b, one branch at one
// instant gave three different answers:
//
//   Stock Home  "All stocked up — no low or out-of-stock items at Dallas"
//   Use stock   "39 items need attention", all 39 reading 0 · Out
//   Catalog     "OUT OF STOCK: 0", while listing 46 items at quantity 0
//
// The Use screen was the only one right, and the rule the other two missed is
// this: LOW requires a reorder point. OUT does not. An item at zero is out
// whether or not anybody ever set a threshold for it. Both broken copies tested
// the threshold FIRST and returned early, so a null reorder point hid an empty
// shelf rather than merely hiding a threshold comparison.
//
// This was not cosmetic for go-live. Counted on production at 78b rather than
// estimated: of 56 stock rows only 4 carry a reorder point, and after #294's
// wipe every count is zero. Dallas would have opened Stock Home on Sep 1 and
// read "All stocked up" over an empty warehouse.
//
// The upstream half is deliberately NOT fixed here: InventoryModals.js hardcodes
// reorder_point: null on every stock row it creates and the create-item form
// never asks for one, so every new item is still born with no threshold to be
// low against. That rides with the company_settings toggles, Oct 1.

// Returns 'Out' | 'Low' | 'OK' | null.
//
// `null` is not a failure and is not the same as 'OK'. It means "this item holds
// stock, but no reorder point is set, so we cannot say whether it is running
// low." 'OK' is a positive claim — the Catalog's status pill makes it — and
// asserting it without a threshold would be inventing a fact.
//
// A missing quantity is deliberately NOT coerced to zero. The Catalog calls this
// with `stock?.qty_on_hand` for an item that may have no stock row at the branch
// at all, and "not stocked at this branch" is a different statement from "out at
// this branch." Coercing would have quietly relabelled every unstocked item Out
// across the whole catalog, which is a scope this issue does not cover.
export function getStockStatus(qty, reorderPoint) {
  if (qty <= 0) return 'Out';
  if (reorderPoint === null || reorderPoint === undefined) return null;
  if (qty <= reorderPoint) return 'Low';
  return 'OK';
}

// The flagged set — what belongs on a low-stock list or a "needs attention"
// count. Callers used to spell this out inline and each spelled it differently.
export function needsAttention(qty, reorderPoint) {
  const status = getStockStatus(qty, reorderPoint);
  return status === 'Out' || status === 'Low';
}
